/**
 * BotaoTorre.ts — Botao flutuante "Construir Torre (N naves)".
 *
 * Aparece quando ha planeta seu selecionado + tem slot livre.
 * Posicionado em coords de tela acima do planeta. Atualizado a cada frame
 * pra acompanhar a camera.
 * Refator de tres.php:1120-1188.
 */

import { corHex } from '../config';
import type {
  Planeta,
  EstadoJogo,
  TamanhoPlaneta,
} from '../domain/tipos';
import type { Camera } from '../render/Camera';
import type { PixiApp } from '../render/PixiApp';

export class BotaoTorre {
  private readonly elemento: HTMLButtonElement;
  private readonly elCusto: HTMLElement;
  private getOrigemSelecionada: () => number | null = () => null;
  private aoClicar: ((planetaId: number) => void) | null = null;

  constructor(
    private readonly estado: EstadoJogo,
    private readonly camera: Camera,
    private readonly pixi: PixiApp,
  ) {
    this.elemento = obrigatorio('swBotaoTorre') as HTMLButtonElement;
    this.elCusto = obrigatorio('swBotaoTorreCusto');

    this.elemento.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const id = this.getOrigemSelecionada();
      if (id !== null && this.aoClicar) this.aoClicar(id);
    });
  }

  /** Liga ao estado de selecao (callback que retorna o id da origem ou null). */
  conectar(getOrigem: () => number | null, aoClicar: (planetaId: number) => void): void {
    this.getOrigemSelecionada = getOrigem;
    this.aoClicar = aoClicar;
  }

  /** Chamar a cada frame (parte do ticker). */
  atualizar(dialogVisivel: boolean): void {
    const origemId = this.getOrigemSelecionada();
    if (origemId === null || dialogVisivel) {
      this.elemento.classList.remove('visivel');
      return;
    }
    const p = this.estado.planetas.get(origemId);
    if (!p || p.donoId !== this.estado.suaCor) {
      this.elemento.classList.remove('visivel');
      return;
    }
    if (p.torres >= p.slotsTorre) {
      this.elemento.classList.remove('visivel');
      return;
    }

    const custo = custoTorreNoPlaneta(p, this.estado);
    this.elCusto.textContent = `(${custo} naves)`;

    if ((p.naves || 0) < custo) {
      this.elemento.classList.add('indisponivel');
    } else {
      this.elemento.classList.remove('indisponivel');
    }

    // Posicao em coords de tela, acima do planeta
    const tela = this.camera.mundoParaTela(p.x, p.y);
    const vp = this.pixi.viewport;
    const sx = Math.max(80, Math.min(vp.w - 80, tela.x));
    const sy = Math.max(40, Math.min(vp.h - 40, tela.y - (p.raio + 30) * this.camera.zoom));
    this.elemento.style.left = `${sx}px`;
    this.elemento.style.top = `${sy}px`;
    this.elemento.style.setProperty('--sw-cor-jogador', corHex(this.estado.suaCor));

    this.elemento.classList.add('visivel');
  }
}

function custoTorreNoPlaneta(p: Planeta, estado: EstadoJogo): number {
  const custos = estado.config?.custos_torre as Record<TamanhoPlaneta, number> | undefined;
  if (custos && p.tamanho && custos[p.tamanho]) return custos[p.tamanho];
  return (estado.config?.custo_torre as number) ?? 5;
}

function obrigatorio(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Elemento #${id} nao encontrado no DOM`);
  return el;
}
